// src/app/time-attendance/yyww/pages/yyww/yyww.component.ts

import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { YywwService } from './yyww.service';
import { Yyww } from '../models/yyww.model';

import { PlanningTopNavComponent } from '../../../shared/planning-nav/planning-top-nav.component';
import { SearchBarComponent } from '../../../shared/search-bar/search-bar.component';

type YywwFormModel = {
  year: number | null;
};

interface YywwFiltersVm {
  searchTerm: string;
}

interface YywwYearGroup {
  year: number;
  weeks: Yyww[];
  isExpanded: boolean;
}

@Component({
  selector: 'app-yyww',
  standalone: true,
  imports: [CommonModule, FormsModule, PlanningTopNavComponent, SearchBarComponent],
  templateUrl: './yyww.component.html',
  styleUrls: ['./yyww.component.scss'],
})
export class YywwComponent implements OnInit, OnDestroy {
  yearGroups: YywwYearGroup[] = [];

  isLoading = false;
  errorMessage: string | null = null;

  filters: YywwFiltersVm = {
    searchTerm: '',
  };

  createModalOpen = false;
  deleteModalOpen = false;

  selectedYearForDelete: number | null = null;
  expandedYears = new Set<number>();

  formModel: YywwFormModel = this.getEmptyForm();

  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';

  constructor(
    private readonly yywwService: YywwService,
    private readonly cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadYywws();
  }

  ngOnDestroy(): void {
    this.unlockBodyScroll();
  }

  private lockBodyScroll(): void {
    document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    document.body.style.overflow = '';
  }

  private syncBodyScrollLock(): void {
    if (this.createModalOpen || this.deleteModalOpen) {
      this.lockBodyScroll();
      return;
    }

    this.unlockBodyScroll();
  }

  loadYywws(onSuccess?: () => void, onFailure?: () => void): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.cd.detectChanges();

    this.yywwService
      .getAllYywws({
        searchTerm: this.filters.searchTerm,
      })
      .subscribe({
        next: (items: Yyww[]) => {
          this.yearGroups = this.groupByYear(items);
          this.isLoading = false;
          this.cd.detectChanges();
          onSuccess?.();
        },
        error: () => {
          this.yearGroups = [];
          this.errorMessage = 'Failed to load YYWW records';
          this.isLoading = false;
          this.cd.detectChanges();
          onFailure?.();
        },
      });
  }

  onSearchTermChange(value: string | null): void {
    this.filters.searchTerm = String(value ?? '').trim();
    this.loadYywws();
  }

  toggleYear(year: number): void {
    if (this.expandedYears.has(year)) {
      this.expandedYears.delete(year);
    } else {
      this.expandedYears.add(year);
    }

    this.yearGroups = this.yearGroups.map((group) =>
      group.year === year
        ? {
            ...group,
            isExpanded: this.expandedYears.has(year),
          }
        : group,
    );

    this.cd.detectChanges();
  }

  openCreateModal(): void {
    this.formModel = this.getEmptyForm();

    this.createModalOpen = true;
    this.syncBodyScrollLock();

    this.cd.detectChanges();
  }

  closeCreateModal(): void {
    this.createModalOpen = false;
    this.formModel = this.getEmptyForm();

    this.syncBodyScrollLock();

    this.cd.detectChanges();
  }

  submitCreate(): void {
    const year = Number(this.formModel.year);

    if (!Number.isInteger(year) || year < 1000 || year > 9999) {
      this.showToast('Year must be exactly 4 digits', 'error');
      return;
    }

    this.isLoading = true;
    this.cd.detectChanges();

    this.yywwService.createYear(year).subscribe({
      next: (success) => {
        if (!success) {
          this.isLoading = false;
          this.closeCreateModal();
          this.showToast('Failed to create YYWW records', 'error');
          this.cd.detectChanges();
          return;
        }

        this.expandedYears.add(year);
        this.closeCreateModal();

        this.loadYywws(
          () => this.showToast(`YYWW records for ${year} created successfully`, 'success'),
          () => this.showToast(`YYWW records were created for ${year}, but refresh failed`, 'error'),
        );
      },
      error: () => {
        this.isLoading = false;
        this.closeCreateModal();
        this.showToast('Failed to create YYWW records', 'error');
        this.cd.detectChanges();
      },
    });
  }

  openDeleteModal(year: number, event?: Event): void {
    event?.stopPropagation();

    this.selectedYearForDelete = year;
    this.deleteModalOpen = true;
    this.syncBodyScrollLock();

    this.cd.detectChanges();
  }

  closeDeleteModal(): void {
    this.deleteModalOpen = false;
    this.selectedYearForDelete = null;

    this.syncBodyScrollLock();

    this.cd.detectChanges();
  }

  confirmDeleteYear(): void {
    if (!this.selectedYearForDelete) {
      this.showToast('No year selected for deletion', 'error');
      return;
    }

    const year = this.selectedYearForDelete;

    this.isLoading = true;
    this.cd.detectChanges();

    this.yywwService.deleteYear(year).subscribe({
      next: (success) => {
        if (!success) {
          this.isLoading = false;
          this.closeDeleteModal();
          this.showToast('Failed to delete YYWW records', 'error');
          this.cd.detectChanges();
          return;
        }

        this.expandedYears.delete(year);
        this.closeDeleteModal();

        this.loadYywws(
          () => this.showToast(`YYWW records for ${year} deleted successfully`, 'success'),
          () => this.showToast(`YYWW records were deleted for ${year}, but refresh failed`, 'error'),
        );
      },
      error: () => {
        this.isLoading = false;
        this.closeDeleteModal();
        this.showToast('Failed to delete YYWW records', 'error');
        this.cd.detectChanges();
      },
    });
  }

  trackByYear(_: number, item: YywwYearGroup): number {
    return item.year;
  }

  trackByYywwId(_: number, item: Yyww): number {
    return item.id;
  }

  showToast(message: string, type: 'success' | 'error'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;
    this.cd.detectChanges();

    setTimeout(() => {
      this.toastVisible = false;
      this.cd.detectChanges();
    }, 2500);
  }

  private groupByYear(items: Yyww[]): YywwYearGroup[] {
    const grouped = new Map<number, Yyww[]>();

    for (const item of items) {
      const existing = grouped.get(item.year) ?? [];
      existing.push(item);
      grouped.set(item.year, existing);
    }

    return Array.from(grouped.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([year, weeks]) => ({
        year,
        weeks: [...weeks].sort((a, b) => a.week - b.week),
        isExpanded: this.expandedYears.has(year),
      }));
  }

  private getEmptyForm(): YywwFormModel {
    return {
      year: null,
    };
  }
}